import { mainIconSize } from '@/constants';
import { useDateFormatter } from '@/hooks';
import { useAppStore } from '@/stores/app';
import type { InstrumentPublic } from '@/types';
import { flushColumnState, sizeColumnsOnFirstVisit } from '@/utils/flushColumnState';
import ErrorMessage from '@components/ErrorMessage';
import { FullLoading } from '@components/Loaders';
import { Button, Tooltip } from '@nautilusbio/nb-design-system';
import { getTextClassName } from '@nautilusbio/nb-design-system/helpers';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import Table from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useLoaderData } from '@tanstack/react-router';
import type { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import { CheckCircle2, Plus, RefreshCw, XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import '../../styles/components/ag-grid-overrides.css';
import { toast } from 'sonner';

import tableStyles from '../../styles/components/table.module.css';
import globalStyles from '../../styles/index.module.css';
import pageStyles from '../../styles/pages/index.module.css';

import { ActionsCellRenderer, InstrumentFormDialog } from './components';
import { useInstruments } from './hooks/useInstruments';

const tableContainerProps = {
  className: clsx(pageStyles.table, globalStyles.scrollbars),
};

export default function InstrumentsPage() {
  const { t } = useTranslation('instruments');
  const { t: tGeneral } = useTranslation('general');
  const formatDate = useDateFormatter();

  const store = useAppStore();
  // Use selectedOrganization (runtime) if available, else fall back to selectedOrgStub (persisted)
  const selectedOrg = store.selectedOrganization || store.selectedOrgStub;
  const orgId = selectedOrg?.id ?? '';

  const loaderDataRaw = useLoaderData({ from: '/instruments/' });
  const initialData = Array.isArray(loaderDataRaw) ? (loaderDataRaw as InstrumentPublic[]) : undefined;

  const { data, isLoading, isPending, error, refetch } = useInstruments(orgId, initialData);
  const rowData: InstrumentPublic[] = data ?? [];

  useEffect(() => {
    if (orgId) refetch();
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const gridRef = useRef<AgGridReact<InstrumentPublic>>(null);

  const handleRefetch = useCallback(async () => {
    const toastId = toast.loading(t('refetching'));
    const result = await refetch();
    if (result.error) {
      toast.error(t('refetch_error'), { id: toastId });
    } else {
      toast.success(t('refetch_success'), { id: toastId });
    }
  }, [refetch, t]);

  const colDefs = useMemo<ColDef<InstrumentPublic>[]>(
    () => [
      {
        field: 'name',
        filter: 'agTextColumnFilter',
        headerName: tGeneral('name'),
      },
      {
        field: 'serial_num',
        filter: 'agTextColumnFilter',
        headerName: t('col_serial_num'),
        hide: true,
      },
      {
        field: 'model',
        filter: 'agTextColumnFilter',
        hide: true,
        headerName: t('col_model'),
      },
      {
        cellClass: tableStyles.tableCellCenter,
        cellRenderer: (params: ICellRendererParams<InstrumentPublic>) =>
          params.value ? (
            <CheckCircle2 aria-label={t('available')} color="var(--color-success)" size={18} />
          ) : (
            <XCircle aria-label={t('unavailable')} color="var(--color-danger)" size={18} />
          ),
        field: 'is_available',
        filter: 'agSetColumnFilter',
        headerName: t('col_is_available'),
        maxWidth: 150,
      },
      {
        field: 'next_pm_at',
        filter: 'agDateColumnFilter',
        headerName: t('col_next_pm_at'),
        valueFormatter: ({ value }: { value: string | null | undefined }) =>
          value ? formatDate(value) : tGeneral('empty'),
      },
      {
        field: 'created_by_full_name',
        filter: 'agTextColumnFilter',
        headerName: tGeneral('created_by'),
      },
      {
        field: 'created_at',
        filter: 'agDateColumnFilter',
        headerName: tGeneral('created_at'),
        hide: true,
        valueFormatter: ({ value }: { value: string }) => formatDate(value),
      },
      {
        field: 'updated_by_full_name',
        filter: 'agTextColumnFilter',
        headerName: tGeneral('updated_by'),
        hide: true,
      },
      {
        field: 'updated_at',
        filter: 'agDateColumnFilter',
        headerName: tGeneral('updated_at'),
        hide: true,
        valueFormatter: ({ value }: { value: string | null | undefined }) =>
          value ? formatDate(value) : tGeneral('empty'),
      },
      {
        cellClass: tableStyles.tableCellCenter,
        cellRenderer: (params: ICellRendererParams<InstrumentPublic>) =>
          params.data ? <ActionsCellRenderer data={params.data} /> : null,
        filter: false,
        headerName: tGeneral('actions'),
        maxWidth: 110,
        resizable: false,
        sortable: false,
        suppressAutoSize: true,
      },
    ],
    [t, tGeneral, formatDate],
  );

  return (
    <Stack alignItems="start" className={pageStyles.organizationContainer} fullWidth>
      {/* Title row */}
      <Stack
        alignItems="center"
        className={clsx(pageStyles.titleRow, pageStyles.organizationTitle)}
        justifyContent="space-between"
        orientation="horizontal"
      >
        <Stack alignItems="center" fullHeight gap={2} justifyContent="center" orientation="horizontal">
          <Text as="div" className={getTextClassName('lg')}>
            {t('title')}
          </Text>

          <Tooltip
            content={t('refetch_tooltip')}
            placement="right-start"
            trigger={
              <Button data-testid="refetch-instruments-btn" onPress={handleRefetch} visual="unstyled">
                <RefreshCw size={16} />
              </Button>
            }
          />
        </Stack>

        <InstrumentFormDialog
          triggerProps={{
            children: (
              <>
                <Plus size={mainIconSize} />
                {t('create_instrument')}
              </>
            ),
            size: 'md',
            visual: 'unstyled',
          }}
        />
      </Stack>

      {/* No separate create dialog needed — trigger is embedded in InstrumentFormDialog above */}

      {(isLoading || isPending) && <FullLoading label={t('fetching')} />}

      <div
        className={clsx(
          pageStyles.organizationList,
          globalStyles.scrollbars,
          (isLoading || isPending) && pageStyles.hide,
          error && pageStyles.errorContainer,
        )}
      >
        {error ? (
          <ErrorMessage error={error} level="error" />
        ) : rowData.length === 0 && !isLoading && !isPending ? (
          <Stack alignItems="center" fullWidth gap={4} justifyContent="center">
            <Text as="p">{t('empty_state')}</Text>
            <InstrumentFormDialog
              triggerProps={{
                children: (
                  <>
                    <Plus size={mainIconSize} />
                    {t('create_instrument')}
                  </>
                ),
                size: 'md',
                visual: 'unstyled',
              }}
            />
          </Stack>
        ) : (
          <Table
            aria-label={t('title')}
            columnDefs={colDefs}
            container={tableContainerProps}
            domLayout="autoHeight"
            enableResponsiveResize={false}
            resizeDebounceMs={150}
            ref={gridRef}
            rowData={rowData}
            tableId="instruments-table"
            onGridReady={e => sizeColumnsOnFirstVisit(e.api, 'instruments-table')}
            onGridPreDestroyed={e => flushColumnState(e.api, 'instruments-table')}
          />
        )}
      </div>
    </Stack>
  );
}
